import React, { useState, useEffect, useCallback } from "react";
import DashboardService from "../../services/DashboardService";
import AuthService from "../../services/AuthService";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const today = () => new Date().toISOString().split("T")[0];
const firstOfYear = () => {
  return new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0];
};

const TABS = ["Source Performance", "Conversion Funnel", "Employee Performance"];

function Reports() {
  const clientId = AuthService.getLoggedInClientId();
  const [activeTab, setActiveTab] = useState(0);
  const [fromDate, setFromDate] = useState(firstOfYear());
  const [toDate, setToDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sourceData, setSourceData] = useState([]);
  const [conversionData, setConversionData] = useState(null);
  const [employeeData, setEmployeeData] = useState([]);

  const loadReports = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const [src, conv, emp] = await Promise.all([
        DashboardService.getSourcePerformance(clientId, fromDate, toDate),
        DashboardService.getConversionReport(clientId, fromDate, toDate),
        DashboardService.getEmployeePerformance(clientId, fromDate, toDate),
      ]);
      setSourceData(src || []);
      setConversionData(conv || null);
      setEmployeeData(emp || []);
    } catch (err) {
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [clientId, fromDate, toDate]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const fmtPct = (val) => (val != null ? `${val}%` : "—");

  return (
    <div style={{ padding: "24px" }}>
      <h4 style={{ marginBottom: 4, fontWeight: 600 }}>CRM Reports</h4>
      <p style={{ color: "#8898aa", fontSize: 13, marginBottom: 24 }}>
        Pipeline and conversion analytics for your client account.
      </p>

      {/* Date range filter */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <label style={{ fontSize: 13, color: "#344767", fontWeight: 500 }}>
          From
        </label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={dateInputStyle}
        />
        <label style={{ fontSize: 13, color: "#344767", fontWeight: 500 }}>
          To
        </label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={dateInputStyle}
        />
        <button onClick={loadReports} style={applyBtnStyle}>
          Apply
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "2px solid #e9ecef", marginBottom: 24 }}>
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            style={{
              background: "none",
              border: "none",
              padding: "8px 20px",
              fontSize: 13,
              fontWeight: activeTab === i ? 600 : 400,
              color: activeTab === i ? "#5e72e4" : "#8898aa",
              borderBottom: activeTab === i ? "2px solid #5e72e4" : "2px solid transparent",
              marginBottom: -2,
              cursor: "pointer",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ color: "#f5365c", marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: "#8898aa", fontSize: 14 }}>Loading...</div>
      ) : (
        <>
          {/* Source Performance */}
          {activeTab === 0 && (
            <div>
              <h6 style={sectionTitle}>Lead Capture by Source</h6>
              <p style={sectionSubtitle}>
                Breakdown of leads, assignments and conversion rate per acquisition source.
              </p>
              {sourceData.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  {/* Charts row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 32 }}>
                    {/* Doughnut — lead share by source */}
                    <div style={chartCard}>
                      <p style={chartTitle}>Lead Share by Source</p>
                      <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Doughnut
                          data={{
                            labels: sourceData.map((r) => r.source || "Unknown"),
                            datasets: [{
                              data: sourceData.map((r) => r.totalLeads),
                              backgroundColor: PALETTE,
                              borderWidth: 2,
                              borderColor: "#fff",
                            }],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "bottom", labels: { font: { size: 11 }, boxWidth: 12, padding: 10 } } },
                            cutout: "60%",
                          }}
                        />
                      </div>
                    </div>

                    {/* Bar — leads vs interested vs deals */}
                    <div style={chartCard}>
                      <p style={chartTitle}>Leads · Interested · Deals Closed</p>
                      <div style={{ height: 240 }}>
                        <Bar
                          data={{
                            labels: sourceData.map((r) => r.source || "Unknown"),
                            datasets: [
                              { label: "Total Leads", data: sourceData.map((r) => r.totalLeads), backgroundColor: "#5e72e4cc", borderRadius: 5 },
                              { label: "Interested", data: sourceData.map((r) => r.interestedLeads), backgroundColor: "#2dce89cc", borderRadius: 5 },
                              { label: "Deals Closed", data: sourceData.map((r) => r.dealsClosedLeads), backgroundColor: "#fb6340cc", borderRadius: 5 },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "bottom", labels: { font: { size: 11 }, boxWidth: 12, padding: 10 } } },
                            scales: { x: { grid: { display: false } }, y: { beginAtZero: true, grid: { color: "#f0f2f5" }, ticks: { font: { size: 11 } } } },
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Detail table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={tableStyle}>
                      <thead>
                        <tr style={theadRowStyle}>
                          {["Source", "Total Leads", "Assigned", "Interested", "Deals Closed", "Conv. Rate"].map(
                            (h) => <th key={h} style={thStyle}>{h}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {sourceData.map((row, i) => (
                          <tr key={i} style={i % 2 === 0 ? trEvenStyle : trOddStyle}>
                            <td style={tdStyle}>
                              <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: PALETTE[i % PALETTE.length], marginRight: 8 }} />
                              {row.source || "Unknown"}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{row.totalLeads}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{row.assignedLeads}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{row.interestedLeads}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{row.dealsClosedLeads}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <ConvBadge value={row.conversionRate} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Conversion Funnel */}
          {activeTab === 1 && (
            <div>
              <h6 style={sectionTitle}>Conversion Funnel</h6>
              <p style={sectionSubtitle}>
                Step-by-step pipeline funnel from total leads to deals closed.
              </p>
              {!conversionData ? (
                <EmptyState />
              ) : (
                <>
                  {/* Summary stats row */}
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
                    {[
                      { label: "Total Leads", value: conversionData.totalLeads },
                      { label: "Assigned", value: conversionData.totalAssigned },
                      { label: "Contacted", value: conversionData.totalContacted },
                      { label: "Interested", value: conversionData.totalInterested },
                      { label: "Visited", value: conversionData.totalVisited },
                      { label: "Deal Done", value: conversionData.totalDealDone },
                      { label: "Closed", value: conversionData.totalClosed },
                    ].map((s) => (
                      <StatCard key={s.label} label={s.label} value={s.value} />
                    ))}
                  </div>
                  {/* Conversion rates */}
                  <h6 style={{ ...sectionTitle, marginTop: 0 }}>Stage-to-Stage Rates</h6>
                  <div style={{ overflowX: "auto" }}>
                    <table style={tableStyle}>
                      <thead>
                        <tr style={theadRowStyle}>
                          {["Stage Transition", "Rate"].map((h) => (
                            <th key={h} style={thStyle}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: "Leads Assigned Rate", val: conversionData.assignedRate },
                          { label: "Contact Rate (of assigned)", val: conversionData.contactRate },
                          { label: "Interest Rate (of contacted)", val: conversionData.interestRate },
                          { label: "Visit Rate (of interested)", val: conversionData.visitRate },
                          { label: "Close Rate (of visited)", val: conversionData.closeRate },
                        ].map((row, i) => (
                          <tr key={i} style={i % 2 === 0 ? trEvenStyle : trOddStyle}>
                            <td style={tdStyle}>{row.label}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <ConvBadge value={row.val} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Employee Performance */}
          {activeTab === 2 && (
            <div>
              <h6 style={sectionTitle}>Team Performance Dashboard</h6>
              <p style={sectionSubtitle}>
                Per-employee assignment, interest, visit and deal metrics for the selected period.
              </p>
              {employeeData.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  {/* Trophy — top performer */}
                  {(() => {
                    const top = [...employeeData].sort((a, b) => b.totalDealDone - a.totalDealDone)[0];
                    return top && top.totalDealDone > 0 ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 14, background: "linear-gradient(135deg,#5e72e4,#825ee4)", borderRadius: 14, padding: "16px 22px", marginBottom: 24, color: "#fff" }}>
                        <span style={{ fontSize: 32 }}>🏆</span>
                        <div>
                          <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>Top Performer This Period</p>
                          <p style={{ margin: "2px 0 0", fontSize: 13, opacity: 0.85 }}>{top.employeeName} — {top.totalDealDone} Deals Closed · {top.conversionRate}% Conv. Rate</p>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Bar chart */}
                  <div style={{ ...chartCard, marginBottom: 24 }}>
                    <p style={chartTitle}>Assigned · Interested · Deals Closed per Employee</p>
                    <div style={{ height: 260 }}>
                      <Bar
                        data={{
                          labels: employeeData.map((r) => r.employeeName.split(" ")[0]),
                          datasets: [
                            { label: "Assigned", data: employeeData.map((r) => r.totalAssigned), backgroundColor: "#5e72e4cc", borderRadius: 5 },
                            { label: "Interested", data: employeeData.map((r) => r.totalInterested), backgroundColor: "#2dce89cc", borderRadius: 5 },
                            { label: "Visited", data: employeeData.map((r) => r.totalVisited), backgroundColor: "#11cdefcc", borderRadius: 5 },
                            { label: "Deal Done", data: employeeData.map((r) => r.totalDealDone), backgroundColor: "#fb6340cc", borderRadius: 5 },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { position: "bottom", labels: { font: { size: 11 }, boxWidth: 12, padding: 10 } } },
                          scales: {
                            x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                            y: { beginAtZero: true, grid: { color: "#f0f2f5" }, ticks: { font: { size: 11 } } },
                          },
                        }}
                      />
                    </div>
                  </div>

                  {/* Detail table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={tableStyle}>
                      <thead>
                        <tr style={theadRowStyle}>
                          {["#", "Employee", "Assigned", "Interested", "Visited", "Deal Done", "Conv. Rate"].map(
                            (h) => <th key={h} style={thStyle}>{h}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {[...employeeData]
                          .sort((a, b) => b.totalDealDone - a.totalDealDone)
                          .map((row, i) => (
                          <tr key={i} style={i % 2 === 0 ? trEvenStyle : trOddStyle}>
                            <td style={{ ...tdStyle, color: i === 0 ? "#fb6340" : "#8898aa", fontWeight: 700 }}>
                              {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                            </td>
                            <td style={{ ...tdStyle, fontWeight: i === 0 ? 700 : 400 }}>{row.employeeName}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{row.totalAssigned}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{row.totalInterested}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>{row.totalVisited}</td>
                            <td style={{ ...tdStyle, textAlign: "center", fontWeight: 600, color: "#2dce89" }}>{row.totalDealDone}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <ConvBadge value={row.conversionRate} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e9ecef",
        borderRadius: 8,
        padding: "16px 20px",
        minWidth: 110,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 24, fontWeight: 700, color: "#344767" }}>{value ?? 0}</div>
      <div style={{ fontSize: 12, color: "#8898aa", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function ConvBadge({ value }) {
  const pct = value ?? 0;
  const color = pct >= 50 ? "#2dce89" : pct >= 20 ? "#fb6340" : "#f5365c";
  return (
    <span
      style={{
        background: color + "20",
        color,
        borderRadius: 12,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {pct}%
    </span>
  );
}

function EmptyState() {
  return (
    <div style={{ color: "#8898aa", fontSize: 13, padding: "32px 0", textAlign: "center" }}>
      No data available for the selected date range.
    </div>
  );
}

const PALETTE = [
  "#5e72e4", "#2dce89", "#fb6340", "#f5365c",
  "#11cdef", "#344767", "#825ee4", "#ffd600",
];

const chartCard = {
  background: "#fff",
  border: "1px solid #e9ecef",
  borderRadius: 12,
  padding: "18px 20px",
};

const chartTitle = {
  fontSize: 12,
  fontWeight: 600,
  color: "#8898aa",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 14,
  marginTop: 0,
};

const dateInputStyle = {
  border: "1px solid #dee2e6",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 13,
  color: "#344767",
};

const applyBtnStyle = {
  background: "#5e72e4",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "6px 18px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const sectionTitle = {
  fontSize: 14,
  fontWeight: 600,
  color: "#344767",
  marginBottom: 4,
  marginTop: 0,
};

const sectionSubtitle = {
  fontSize: 12,
  color: "#8898aa",
  marginBottom: 16,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const theadRowStyle = {
  background: "#f6f9fc",
};

const thStyle = {
  padding: "10px 12px",
  fontWeight: 600,
  color: "#8898aa",
  textAlign: "left",
  borderBottom: "1px solid #e9ecef",
  textTransform: "uppercase",
  fontSize: 11,
  letterSpacing: "0.5px",
};

const tdStyle = {
  padding: "10px 12px",
  color: "#344767",
  borderBottom: "1px solid #f6f9fc",
};

const trEvenStyle = { background: "#fff" };
const trOddStyle = { background: "#fafbfc" };

export default Reports;
